import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Play, Pause, AlertCircle, Download, Loader2 } from 'lucide-react';
import { useT } from '../utils/translate';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const SPEEDS = [1, 1.25, 1.5, 2];

function fmtT(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// Qo'ng'iroq yozuvi pleyeri — Calls, Contact/Deal detail sahifalarining
// barchasida shu ishlatiladi: progress bar bo'ylab bosib/sudrab o'tkazib
// eshitish va tezlikni (1x/1.25x/1.5x/2x) o'zgartirish imkoni bilan.
// <audio> elementi faqat birinchi "Play" bosilgandagina yaratiladi - aks holda
// ro'yxatdagi har bir qatorga darhol audio elementi yaratilib, brauzerning
// "WebMediaPlayer" chegarasiga tez urilib qolar edi.
export default function CallRecordingPlayer({ url, callId }) {
  const t = useT();
  const [activated, setActivated] = useState(false);
  const [playing,   setPlaying]   = useState(false);
  const [current,   setCurrent]   = useState(0);
  const [duration,  setDuration]  = useState(0);
  const [speed,     setSpeed]     = useState(1);
  const [dragging,  setDragging]  = useState(false);
  const [error,     setError]     = useState(false);
  const [saving,    setSaving]    = useState(false);
  const audioRef = useRef(null);
  const barRef   = useRef(null);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, activated]);

  if (!url) return <span className="text-xs text-ink-tertiary">—</span>;

  const toggle = (e) => {
    e?.stopPropagation();
    if (error) return;
    if (!activated) { setActivated(true); setPlaying(true); return; }
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const handleError = () => {
    setPlaying(false);
    setError(true);
    toast.error(t('loc.callRecordingPlayer.couldNotLoadTheCall'));
  };

  const ratioFromEvent = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  };

  const seekTo = (ratio) => {
    if (!audioRef.current || !duration) return;
    const t = ratio * duration;
    audioRef.current.currentTime = t;
    setCurrent(t);
  };

  const onBarPointerDown = (e) => {
    e.stopPropagation();
    if (!duration) return;
    setDragging(true);
    seekTo(ratioFromEvent(e));
    const onMove = (ev) => seekTo(ratioFromEvent(ev));
    const onUp = () => {
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Скачивание записи: ссылка на запись лежит на домене АТС (CORS), поэтому файл отдаёт наш сервер.
  const download = async (e) => {
    e.stopPropagation();
    if (saving) return;
    if (!callId) { window.open(url, '_blank', 'noopener'); return; }
    setSaving(true);
    try {
      // Ba'zi ATS (Sipuni) faylni serverimizga juda sekin beradi — 25 soniyada kelmasa, brauzer to'g'ridan-to'g'ri ochadi
      const r = await axios.get(`${API}/atc/calls/${callId}/recording`, { responseType: 'blob', timeout: 25000 });
      const m = /filename="?([^";]+)"?/i.exec(r.headers['content-disposition'] || '');
      const href = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = href; a.download = m?.[1] || `call-${callId}.mp3`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch {
      // Server orqali bo'lmadi — yozuvni brauzerning o'zi ochadi (ATS havolasi ommaviy, foydalanuvchi uni saqlab oladi)
      if (url && /^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
      else toast.error(t('loc.callRecordingPlayer.downloadFailed'));
    } finally { setSaving(false); }
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    const i = SPEEDS.indexOf(speed);
    setSpeed(SPEEDS[(i + 1) % SPEEDS.length]);
  };

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
      {activated && (
        <audio ref={audioRef} src={url} autoPlay preload="none"
          onLoadedMetadata={e => setDuration(e.target.duration || 0)}
          onTimeUpdate={e => { if (!dragging) setCurrent(e.target.currentTime); }}
          onEnded={() => { setPlaying(false); setCurrent(0); }}
          onError={handleError}
        />
      )}
      <button type="button" onClick={toggle} title={error ? t('loc.callRecordingPlayer.couldNotLoadTheRecording') : undefined}
        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors shrink-0 ${
          error ? 'bg-red-50 text-red-600 cursor-not-allowed' : 'bg-primary-50 hover:bg-primary-100 text-primary-600'
        }`}>
        {error ? <AlertCircle className="w-3.5 h-3.5" /> : playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <button type="button" onClick={download} disabled={saving} title={t('loc.callRecordingPlayer.download')}
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-ink-tertiary hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      </button>
      {activated && !error && (
        <>
          <div ref={barRef} onPointerDown={onBarPointerDown}
            className="relative h-1.5 bg-surface-200 rounded-full cursor-pointer flex-1 min-w-[64px] max-w-[140px]">
            <div className="absolute top-0 left-0 h-full bg-primary-500 rounded-full pointer-events-none" style={{ width: `${pct}%` }} />
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary-600 shadow pointer-events-none"
              style={{ left: `${pct}%` }} />
          </div>
          <span className="text-xs text-ink-tertiary font-mono tabular-nums shrink-0">
            {fmtT(current)}/{fmtT(duration)}
          </span>
          <button type="button" onClick={cycleSpeed} title={t('loc.callRecordingPlayer.speed')}
            className="text-xs font-semibold text-ink-tertiary hover:text-primary-600 bg-surface-100 hover:bg-primary-50 px-1.5 py-0.5 rounded-md shrink-0 transition-colors">
            {speed}x
          </button>
        </>
      )}
    </div>
  );
}
